import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, TextInput, Select, Field, Modal,
} from "@/components/common/UI";
import { Users, Plus, Search, KeyRound, UserX, UserCheck, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const ROLE_DESCRIPTIONS = {
    CORPORATE_ADMIN: "Full access — manages all master data and users.",
    FINANCE_PRICING: "Creates ZBCs and RFQs. Cannot change master rates.",
    DEPT_HEAD: "Approves RFQs only. Read-only on costing data.",
    PLANT_MANAGER: "Enters machine rates for their plant only.",
    PURCHASE_MANAGER: "View-only across all modules.",
    DIRECTOR: "Approves settlements and low-margin RFQs.",
};

const ROLE_TONE = {
    CORPORATE_ADMIN: "slate",
    FINANCE_PRICING: "blue",
    DEPT_HEAD: "blue",
    PLANT_MANAGER: "amber",
    PURCHASE_MANAGER: "slate",
    DIRECTOR: "emerald",
};

export default function UserManagement() {
    const [users, setUsers] = useState(null);
    const [plants, setPlants] = useState([]);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [filterRole, setFilterRole] = useState("ALL");
    const [filterStatus, setFilterStatus] = useState("ACTIVE");
    const [editing, setEditing] = useState(null); // null | {} for new | user obj for edit
    const [resetTarget, setResetTarget] = useState(null);
    const [deactTarget, setDeactTarget] = useState(null);

    const load = () =>
        Promise.all([api.get("/users"), api.get("/plants")])
            .then(([u, p]) => { setUsers(u.data); setPlants(p.data); })
            .catch((e) => setErr(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!users) return null;
        return users.filter((u) => {
            if (search && !`${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase())) return false;
            if (filterRole !== "ALL" && u.role !== filterRole) return false;
            if (filterStatus === "ACTIVE" && !u.is_active) return false;
            if (filterStatus === "INACTIVE" && u.is_active) return false;
            return true;
        });
    }, [users, search, filterRole, filterStatus]);

    return (
        <div data-testid="user-management-page">
            <PageHeader
                eyebrow="ADMINISTRATION"
                title="User Management"
                subtitle="Add, edit and manage user accounts. Each user has one role and defined plant access."
                actions={
                    <CopperButton onClick={() => setEditing({})} data-testid="user-add-btn">
                        <Plus className="h-4 w-4" /> Add User
                    </CopperButton>
                }
            />

            <Card className="p-3 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="relative col-span-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <TextInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="pl-8 h-8 text-[12px]" data-testid="user-search-input" />
                    </div>
                    <Select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="h-8 text-[12px]" data-testid="user-filter-role">
                        <option value="ALL">All roles</option>
                        {Object.keys(ROLE_DESCRIPTIONS).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </Select>
                    <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 text-[12px]" data-testid="user-filter-status">
                        <option value="ACTIVE">Active only</option>
                        <option value="INACTIVE">Inactive only</option>
                        <option value="ALL">All statuses</option>
                    </Select>
                </div>
            </Card>

            {err && <ErrorState message={err} onRetry={load} />}
            {!err && users === null && <LoadingState />}
            {filtered && filtered.length === 0 && (
                <EmptyState
                    icon={Users}
                    title="No users match these filters"
                    description="Adjust filters or add a new user."
                    action={<CopperButton onClick={() => setEditing({})}><Plus className="h-4 w-4" /> Add User</CopperButton>}
                />
            )}

            {filtered && filtered.length > 0 && (
                <Card>
                    <table className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-3 px-3">Name</th>
                                <th className="eyebrow text-left py-3 px-3">Email</th>
                                <th className="eyebrow text-left py-3 px-3">Role</th>
                                <th className="eyebrow text-left py-3 px-3">Plant Access</th>
                                <th className="eyebrow text-left py-3 px-3">Status</th>
                                <th className="eyebrow text-left py-3 px-3">Created</th>
                                <th className="eyebrow text-right py-3 px-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u) => (
                                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                                    <td className="py-2.5 px-3 text-[13px]">
                                        <div className="font-semibold text-slate-900">{u.name}</div>
                                        {u.title && <div className="text-[11px] text-slate-500">{u.title}</div>}
                                    </td>
                                    <td className="py-2.5 px-3 text-[12px] font-mono text-slate-600">{u.email}</td>
                                    <td className="py-2.5 px-3"><Pill tone={ROLE_TONE[u.role]}>{u.role.replace(/_/g, " ")}</Pill></td>
                                    <td className="py-2.5 px-3 text-[12px]">{u.plant_code || <span className="text-slate-400">All Plants</span>}</td>
                                    <td className="py-2.5 px-3">{u.is_active ? <Pill tone="emerald">ACTIVE</Pill> : <Pill tone="slate">INACTIVE</Pill>}</td>
                                    <td className="py-2.5 px-3 text-[11px] text-slate-500 font-mono">{fmtDate(u.created_at)}</td>
                                    <td className="py-2.5 px-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <SlateButton onClick={() => setEditing(u)} variant="ghost" className="h-7 px-2 text-[11px]" data-testid={`user-edit-${u.email}`}><Edit3 className="h-3 w-3" /> Edit</SlateButton>
                                            <SlateButton onClick={() => setResetTarget(u)} variant="ghost" className="h-7 px-2 text-[11px]"><KeyRound className="h-3 w-3" /></SlateButton>
                                            {u.is_active ? (
                                                <SlateButton onClick={() => setDeactTarget(u)} variant="ghost" className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50" data-testid={`user-deactivate-${u.email}`}><UserX className="h-3 w-3" /></SlateButton>
                                            ) : (
                                                <SlateButton onClick={async () => { try { await api.post(`/users/${u.id}/activate`); toast.success(`Activated ${u.name}`); load(); } catch (e) { toast.error(formatApiError(e)); } }} variant="ghost" className="h-7 px-2 text-[11px] text-emerald-600 hover:bg-emerald-50"><UserCheck className="h-3 w-3" /></SlateButton>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            {editing !== null && (
                <UserFormModal
                    user={editing}
                    plants={plants}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
            {resetTarget && (
                <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
            )}
            {deactTarget && (
                <DeactivateModal user={deactTarget} onClose={() => setDeactTarget(null)} onDone={() => { setDeactTarget(null); load(); }} />
            )}
        </div>
    );
}

function UserFormModal({ user, plants, onClose, onSaved }) {
    const isNew = !user.id;
    const [form, setForm] = useState({
        name: user.name || "",
        email: user.email || "",
        role: user.role || "FINANCE_PRICING",
        plant_id: user.plant_id || "",
        title: user.title || "",
        is_active: user.is_active !== false,
        password: "",
        confirm: "",
    });
    const [emailErr, setEmailErr] = useState("");
    const [busy, setBusy] = useState(false);
    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const isPlantManager = form.role === "PLANT_MANAGER";

    const validate = () => {
        if (!form.name.trim()) return "Name is required";
        if (!form.email.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Invalid email format";
        if (isPlantManager && !form.plant_id) return "Plant Manager must be assigned to a plant";
        if (isNew) {
            if (form.password.length < 8) return "Password must be at least 8 characters";
            if (form.password !== form.confirm) return "Passwords do not match";
        }
        return null;
    };

    const save = async () => {
        const e = validate();
        if (e) { toast.error(e); return; }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                role: form.role,
                plant_id: isPlantManager ? form.plant_id : null,
                title: form.title.trim(),
                is_active: form.is_active,
            };
            if (isNew) {
                payload.password = form.password;
                await api.post("/users", payload);
                toast.success("User created");
            } else {
                await api.put(`/users/${user.id}`, payload);
                toast.success("User updated");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <Modal
            open={true} onClose={onClose}
            title={isNew ? "Add User" : `Edit ${user.name}`}
            size="lg"
            footer={
                <>
                    <SlateButton onClick={onClose} variant="ghost">Cancel</SlateButton>
                    <CopperButton onClick={save} disabled={busy} data-testid="user-save-btn">{isNew ? "Create User" : "Save Changes"}</CopperButton>
                </>
            }
        >
            <div className="space-y-4 text-[13px]">
                <div className="eyebrow text-slate-500">USER ACCOUNT</div>
                <Field label="Full Name" required>
                    <TextInput value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="e.g. Demo User" data-testid="user-form-name" />
                </Field>
                <Field label="Email" required>
                    <TextInput
                        type="email" value={form.email} onChange={(e) => upd("email", e.target.value)}
                        onBlur={() => setEmailErr(!form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? "" : "Invalid email format")}
                        placeholder="user@testemail.com" disabled={!isNew}
                        data-testid="user-form-email"
                    />
                    {emailErr && <div className="text-[11px] text-red-600 mt-1">{emailErr}</div>}
                </Field>
                <Field label="Title (optional)">
                    <TextInput value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="e.g. Head of Pricing" />
                </Field>
                <Field label="Role" required>
                    <Select value={form.role} onChange={(e) => upd("role", e.target.value)} data-testid="user-form-role">
                        {Object.keys(ROLE_DESCRIPTIONS).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </Select>
                    <div className="text-[11px] text-slate-500 mt-1 italic">{ROLE_DESCRIPTIONS[form.role]}</div>
                </Field>
                <Field label="Plant Access" required={isPlantManager}>
                    {isPlantManager ? (
                        <>
                            <Select value={form.plant_id} onChange={(e) => upd("plant_id", e.target.value)} data-testid="user-form-plant">
                                <option value="">— Select a plant —</option>
                                {plants.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                            </Select>
                            <div className="text-[11px] text-slate-500 mt-1 italic">Plant Managers are restricted to their assigned plant only.</div>
                        </>
                    ) : (
                        <>
                            <TextInput value="All Plants" disabled />
                            <div className="text-[11px] text-slate-500 mt-1 italic">This role has access to all plants.</div>
                        </>
                    )}
                </Field>

                {isNew ? (
                    <>
                        <div className="eyebrow text-slate-500 pt-2">PASSWORD</div>
                        <Field label="Password" required>
                            <TextInput type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} placeholder="Min 8 characters" data-testid="user-form-password" />
                        </Field>
                        <Field label="Confirm Password" required>
                            <TextInput type="password" value={form.confirm} onChange={(e) => upd("confirm", e.target.value)} data-testid="user-form-password-confirm" />
                        </Field>
                    </>
                ) : (
                    <div className="pt-2 text-[12px] text-slate-500 italic">Use the key icon in the list to reset this user&rsquo;s password.</div>
                )}
            </div>
        </Modal>
    );
}

function ResetPasswordModal({ user, onClose }) {
    const [pwd, setPwd] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        setBusy(true);
        try { await api.post(`/users/${user.id}/reset-password`, { new_password: pwd }); toast.success(`Password reset for ${user.name}`); onClose(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Modal
            open={true} onClose={onClose}
            title={`Reset password — ${user.name}`}
            footer={
                <>
                    <SlateButton onClick={onClose} variant="ghost">Cancel</SlateButton>
                    <CopperButton onClick={submit} disabled={busy} data-testid="user-reset-confirm">Reset Password</CopperButton>
                </>
            }
        >
            <Field label="New Password" required>
                <TextInput type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 8 characters" data-testid="user-reset-password-input" />
            </Field>
            <div className="text-[11px] text-slate-500 mt-2 italic">Communicate this new password to the user securely.</div>
        </Modal>
    );
}

function DeactivateModal({ user, onClose, onDone }) {
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try { await api.post(`/users/${user.id}/deactivate`); toast.success(`Deactivated ${user.name}`); onDone(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Modal
            open={true} onClose={onClose}
            title="Confirm Deactivation"
            footer={
                <>
                    <SlateButton onClick={onClose} variant="ghost">Cancel</SlateButton>
                    <SlateButton onClick={submit} disabled={busy} variant="danger" data-testid="user-deactivate-confirm">Deactivate</SlateButton>
                </>
            }
        >
            <div className="space-y-2 text-[13px]">
                <p>Deactivating <strong>{user.name}</strong> will:</p>
                <ul className="list-disc list-inside text-slate-600 text-[12px] space-y-1 ml-2">
                    <li>Immediately revoke their login access</li>
                    <li>Reassign any pending approvals to other approvers (DH/Director)</li>
                    <li>Keep all historical records they created (cannot be deleted)</li>
                </ul>
                <p className="text-slate-500 text-[12px] italic pt-2">You can reactivate them anytime from the list.</p>
            </div>
        </Modal>
    );
}
